import React from "react";
import { Button } from "../ui/button";
import { Linkedin, Github } from "lucide-react";
import { GlowingEffect } from "../ui/glowing-effect";
import resumeEN from "../../Data/hediboumaizaresume.pdf";
import resumeFR from "../../Data/hediboumaizaresumefr.pdf";
import { useLang } from "../../context/LanguageContext";
import { t } from "../../translations";

export default function Contact() {
    const { lang } = useLang();
    const tr = t[lang].contact;
    const resume = lang === "fr" ? resumeFR : resumeEN;
    const resumeFilename = lang === "fr" ? "Hedi_Bou_Maiza_CV_FR.pdf" : "Hedi_Bou_Maiza_Resume_EN.pdf";
    return (
        <section id="contact" className="w-full bg-surface transition-colors duration-200">
            <div className="max-w-5xl mx-auto pt-12 sm:pt-16 md:pt-24 px-4 sm:px-8 md:px-16 pb-12 sm:pb-16 md:pb-24">
                <div className="max-w-5xl mx-auto bg-surface-elevated border border-separator rounded-3xl shadow-card p-6 sm:p-10 relative overflow-hidden group">
                    <GlowingEffect
                        spread={70}
                        glow={false}
                        disabled={false}
                        proximity={120}
                        inactiveZone={0.01}
                        borderWidth={3}
                        blur={4}
                    />
                    <div className="relative z-10">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-8 text-center text-label">
                            {tr.title}
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                            {/* Contact Information */}
                            <div className="space-y-5">
                                <h3 className="text-lg font-semibold tracking-tight mb-4 text-label">
                                    {tr.infoTitle}
                                </h3>

                                {/* Email */}
                                <a href="mailto:hedi.boumaiza@etudiant-isi.utm.tn" className="flex items-center gap-4 min-h-[44px] text-label-secondary hover:text-accent-text transition-colors">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-6 w-6 shrink-0"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                                        />
                                    </svg>
                                    <span className="text-[15px]">hedi.boumaiza@etudiant-isi.utm.tn</span>
                                </a>

                                {/* WhatsApp */}
                                <a href="tel:+21653420089" className="flex items-center gap-4 min-h-[44px] text-label-secondary hover:text-accent-text transition-colors">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-6 w-6 shrink-0"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                                    </svg>
                                    <span className="text-[15px]">+216 53 420 089</span>
                                </a>
                            </div>

                            {/* Social Links & Resume */}
                            <div className="space-y-5">
                                <h3 className="text-lg font-semibold tracking-tight mb-4 text-label">
                                    {tr.connectTitle}
                                </h3>

                                <div className="flex flex-col space-y-2">
                                    {/* LinkedIn */}
                                    <a
                                        href="https://www.linkedin.com/in/hedi-bou-maiza/"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-4 min-h-[44px] text-label-secondary hover:text-accent-text transition-colors"
                                    >
                                        <Linkedin className="h-6 w-6 shrink-0" />
                                        <span className="text-[15px]">LinkedIn Profile</span>
                                    </a>

                                    {/* GitHub */}
                                    <a
                                        href="https://github.com/hediipor"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="flex items-center gap-4 min-h-[44px] text-label-secondary hover:text-accent-text transition-colors"
                                    >
                                        <Github className="h-6 w-6 shrink-0" />
                                        <span className="text-[15px]">GitHub Profile</span>
                                    </a>
                                </div>

                                {/* Resume Button */}
                                <div className="mt-6">
                                    <Button
                                        className="w-full sm:w-auto"
                                        onClick={() => {
                                            const link = document.createElement("a");
                                            link.href = resume;
                                            link.download = resumeFilename;
                                            document.body.appendChild(link);
                                            link.click();
                                            document.body.removeChild(link);
                                        }}
                                    >
                                        <span className="flex items-center justify-center gap-2">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-[18px] w-[18px]"
                                                viewBox="0 0 20 20"
                                                fill="currentColor"
                                            >
                                                <path
                                                    fillRule="evenodd"
                                                    d="M6 2a2 2 0 00-2 2v12a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2H6zm5 6a1 1 0 10-2 0v3.586l-1.293-1.293a1 1 0 10-1.414 1.414l3 3a1 1 0 001.414 0l3-3a1 1 0 00-1.414-1.414L11 11.586V8z"
                                                    clipRule="evenodd"
                                                />
                                            </svg>
                                            <span>{tr.downloadResume}</span>
                                        </span>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 text-center text-label-secondary">
                            <p className="text-[15px] leading-relaxed">{tr.blurb}</p>
                            <p className="text-[15px] font-medium mt-2 text-label">{tr.cta}</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
